<template lang="pug">
.endDocumentRemindHistoryTab
  .register-body.mt-4
    ResendHistoryTable(
      v-model="items"
      :fields="fields"
      :totalRows.sync="rows"
      :currentPage="currentPage"
      :perPage="perPage"
      :isLoading="_isBusy"
      @onShowTarget="showTarget($event, questionnaire)"
    )
    .table-options.d-flex.flex-row.justify-content-end.align-items-center
      // ページあたりの行数
      span.table-option-text 1ページの行数:
      .per-page
        b-form-select(
          id="per-page-select"
          v-model="perPage"
          :options="pageOptions"
          size="md"
          @change="selectPerPage"
        )
      // ページネーション
      b-pagination.m-0(
        v-model="currentPage"
        :total-rows="rows"
        :per-page="perPage"
        :disabled="isBusy"
        @change="selectPage"
        hide-goto-end-buttons
      )

  TargetFamilyDetailModal(v-model="showTargetFamilyListModal" :items="targetFamilyListItems")
  TargetOtherUserDetailModal(v-model="showTargetOtherUserListModal" :items="targetOtherUserListItems")
</template>

<script lang="ts">
import { Component, Watch, Inject, Prop, Vue } from "vue-property-decorator";

import Setting from "../../../../public/setting.json";

import QuestionnairesService, {
  GroupType,
  OtherUserGroupInfo,
  QuestionnairesGetModel,
  ResidentGroupInfo,
  SendMessageGroupInfo,
  TargetType
} from "@/commons/services/Questionnaires";
import { FamilyGetModel } from "@/commons/services/Families";
import OtherUserGroup from "@/commons/models/OtherUserGroup";

import ResendHistoryTable, { ResendHistoryTableField, ResendHistoryTableItem } from "@/pc/containers/questionnaires/ResendHistoryTable.vue";
import TargetFamilyDetailModal from "@/pc/containers/questionnaires/TargetFamilyDetailModal.vue";
import { TargetFamilyDetailTableItem } from "@/pc/containers/questionnaires/TargetFamilyDetailTable.vue";
import TargetOtherUserDetailModal from "@/pc/containers/questionnaires/TargetOtherUserDetailModal.vue";
import { TargetOtherUserDetailTableItem } from "@/pc/containers/questionnaires/TargetOtherUserDetailTable.vue";

/**
 * ### 文書送付確認画面コンポーネント
 */
@Component({
  components: {
    ResendHistoryTable,
    TargetFamilyDetailModal,
    TargetOtherUserDetailModal
  }
})
export default class SendDocumentRemindHistoryTab extends Vue {
  /** アンケートサービス */
  @Inject("QuestionnairesService") questionnairesService!: QuestionnairesService;

  /** 読込み中か否か */
  @Prop()
  isBusy!: boolean;
  /** 読込み中か否か */
  @Prop()
  questionnaire!: QuestionnairesGetModel;
  /** ソートキーとオブジェクトのMAP */
  @Prop()
  usersMap!: Map<string, FamilyGetModel | OtherUserGroup>;

  /** テーブル列名 */
  fields: Array<ResendHistoryTableField> = [];
  /** テーブル各行の情報 */
  items: Array<ResendHistoryTableItem> = [];

  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.questionnairesDetail.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.questionnairesDetail.pageOptions;
  /** 先行して取得するページ範囲 */
  pageRange: number = Setting.questionnairesDetail.pageRange;

  /** 宛先一覧モーダル(家族)表示可否 */
  showTargetFamilyListModal: boolean = false;
  /** 宛先一覧モーダル(家族) */
  targetFamilyListItems: Array<TargetFamilyDetailTableItem> = [];
  /** 宛先一覧モーダル(施設関係者)表示可否 */
  showTargetOtherUserListModal: boolean = false;
  /** 宛先一覧モーダル(施設関係者) */
  targetOtherUserListItems: Array<TargetOtherUserDetailTableItem> = [];

  get _isBusy(): boolean {
    return this.isBusy;
  }
  set _isBusy(newValue: boolean) {
    this.$emit("update:isBusy", newValue);
    return;
  }

  @Watch("questionnaire")
  changeQuestionnaire(): void {
    if (!this.questionnaire) {
      this.items = [];
      this.rows = 0;
    }
    const items: Array<ResendHistoryTableItem> = [];
    for (const history of this.questionnaire.histories) {
      items.push({
        // 表示で使用するのでアンケートの情報も格納する
        targetType: this.questionnaire.targetType,
        // 履歴
        messageTitle: history.messageTitle,
        messageText: history.messageText,
        targets: history.targets,
        targetNames: this.convertSendTargetLabel(
          history.targets,
          this.questionnaire.targetType,
          history.sendMessageGroupInfos,
          history.residentGroupInfos,
          history.otherUserGroupInfos
        ),
        lineTargets: history.lineTargets,
        mailTargets: history.mailTargets,
        sendMessageGroupInfos: history.sendMessageGroupInfos,
        residentGroupInfos: history.residentGroupInfos,
        otherUserGroupInfos: history.otherUserGroupInfos,
        messagesSortKey: history.messagesSortKey,
        sender: history.sender,
        senderName: history.senderName,
        sendDatetime: history.sendDatetime
      });
    }
    this.items = items;
    this.rows = items.length;
    return;
  }

  /** ### 宛先文字列を返す */
  public convertSendTargetLabel(
    sortKeys: string[],
    targetType: TargetType,
    sendMessageGroupInfos: Array<SendMessageGroupInfo>,
    residentGroupInfos: Array<ResidentGroupInfo>,
    otherUserGroupInfos: Array<OtherUserGroupInfo>
  ): string[] {
    let sendTargets: string[] = [];
    switch (targetType) {
      case TargetType.ALL_FAMILIES: {
        sendTargets.push("家族全員");
        break;
      }
      case TargetType.FAMILIES: {
        const userMap: Map<string, FamilyGetModel> = this.usersMap as Map<string, FamilyGetModel>;
        for (const key of sortKeys) {
          const user: FamilyGetModel | undefined = userMap.get(key);
          if (user) {
            sendTargets.push(`${user.familyName}（${user.room}・${user.name}）`);
          }
        }
        break;
      }
      case TargetType.OTHER_USERS: {
        const userMap: Map<string, OtherUserGroup> = this.usersMap as Map<string, OtherUserGroup>;
        for (const key of sortKeys) {
          const user: OtherUserGroup | undefined = userMap.get(key);
          if (user) {
            sendTargets.push(`${user.memberName}`);
          }
        }
        break;
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        for (const groupInfo of sendMessageGroupInfos) {
          sendTargets.push(groupInfo.name);
        }
        break;
      }
      case TargetType.RESIDENT_GROUPS: {
        for (const groupInfo of residentGroupInfos) {
          sendTargets.push(groupInfo.name);
        }
        break;
      }
      case TargetType.OTHER_USER_GROUPS: {
        for (const groupInfo of otherUserGroupInfos) {
          sendTargets.push(groupInfo.name);
        }
        break;
      }
    }
    return sendTargets;
  }

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  public setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `questionnaires-detail-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle created
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<ResendHistoryTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.room.perPage);
    return;
  }

  /**
   * ### テーブル列名を作成する
   */
  public createFields(): Array<ResendHistoryTableField> {
    let fields: Array<ResendHistoryTableField> = [];
    fields.push({ key: "sendDatetime", label: "再送日時", sortable: false, sortDirection: "desc", class: "send-datetime text-center" });
    fields.push({ key: "targets", label: "宛先", sortable: false, sortDirection: "desc", class: "targets" });
    fields.push({ key: "messageTitle", label: "タイトル", sortable: false, sortDirection: "desc", class: "title" });
    fields.push({ key: "messageText", label: "本文", sortable: false, sortDirection: "desc", class: "text" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  public loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["questionnaires-detail-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  /**
   * ### 宛先一覧ボタン押下イベント
   */
  public showTarget(item: ResendHistoryTableItem, questionnaire: QuestionnairesGetModel | undefined): void {
    if (!questionnaire) {
      return;
    }

    this.setReceiverDetails(item, questionnaire);

    switch (questionnaire.targetType) {
      case TargetType.ALL_FAMILIES:
      case TargetType.FAMILIES: {
        this.showTargetFamilyListModal = true;
        break;
      }
      case TargetType.OTHER_USERS: {
        this.showTargetOtherUserListModal = true;
        break;
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        for (const groupInfo of questionnaire.sendMessageGroupInfos) {
          if (groupInfo.type == GroupType.FAMILY) {
            this.showTargetFamilyListModal = true;
            break;
          } else {
            this.showTargetOtherUserListModal = true;
            break;
          }
        }
        break;
      }
      case TargetType.RESIDENT_GROUPS: {
        this.showTargetFamilyListModal = true;
        break;
      }
      case TargetType.OTHER_USER_GROUPS: {
        this.showTargetOtherUserListModal = true;
        break;
      }
    }
    return;
  }

  /**
   * ### 宛先詳細モーダルで表示する宛先情報を格納する
   */
  public setReceiverDetails(item: ResendHistoryTableItem, questionnaire: QuestionnairesGetModel): void {
    this.targetFamilyListItems = [];
    this.targetOtherUserListItems = [];

    switch (questionnaire.targetType) {
      case TargetType.ALL_FAMILIES:
      case TargetType.FAMILIES: {
        this.targetFamilyListItems = this.createFamilyReceiverDetails(item.targets);
        break;
      }
      case TargetType.OTHER_USERS: {
        this.targetOtherUserListItems = this.createOtherUserReceiverDetails(item.targets);
        break;
      }
      case TargetType.SEND_MESSAGE_GROUPS: {
        for (const groupInfo of item.sendMessageGroupInfos) {
          if (groupInfo.type == GroupType.FAMILY) {
            this.targetFamilyListItems = this.targetFamilyListItems.concat(this.createFamilyReceiverDetails(groupInfo.memberSortKeys));
          } else {
            this.targetOtherUserListItems = this.targetOtherUserListItems.concat(this.createOtherUserReceiverDetails(groupInfo.memberSortKeys));
          }
        }
        break;
      }
      case TargetType.RESIDENT_GROUPS: {
        for (const groupInfo of item.residentGroupInfos) {
          this.targetFamilyListItems = this.targetFamilyListItems.concat(this.createFamilyReceiverDetails(groupInfo.residentsSortKeys));
        }
        break;
      }
      case TargetType.OTHER_USER_GROUPS: {
        for (const groupInfo of item.otherUserGroupInfos) {
          this.targetOtherUserListItems = this.targetOtherUserListItems.concat(this.createOtherUserReceiverDetails(groupInfo.memberSortKeys));
        }
        break;
      }
    }

    return;
  }

  public createFamilyReceiverDetails(targets: string[]): Array<TargetFamilyDetailTableItem> {
    let receiverDetails: Array<TargetFamilyDetailTableItem> = [];

    const userMap: Map<string, FamilyGetModel> = this.usersMap as Map<string, FamilyGetModel>;
    for (const key of targets) {
      const user: FamilyGetModel | undefined = userMap.get(key);
      if (user) {
        receiverDetails.push({ ...user });
      }
    }

    return receiverDetails;
  }

  public createOtherUserReceiverDetails(targets: string[]): Array<TargetOtherUserDetailTableItem> {
    let receiverDetails: Array<TargetOtherUserDetailTableItem> = [];

    const userMap: Map<string, OtherUserGroup> = this.usersMap as Map<string, OtherUserGroup>;
    for (const key of targets) {
      const user: OtherUserGroup | undefined = userMap.get(key);
      if (user) {
        receiverDetails.push({ ...user });
      }
    }

    return receiverDetails;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   * - ページあたりの表示行数が変わるので表の情報を作り直す
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }
}
</script>
